<template>
  <div>
    <TopBarTitleComponent title="Documentos">
      <template slot="right-area">
        <div
          class="d-flex justify-content-end align-items-center"
          style="gap:10px"
        >
          <button
            class="doc-btn-validate"
            @click="goRouter('documentos.validate')"
          >
            <i class="el-icon-document-checked"></i> Validar
          </button>
          <button
            v-if="$store.getters.canSign && $can('documents.sign')"
            class="doc-btn-primary"
            @click="goRouter('documentos.new')"
          >
            <i class="el-icon-plus"></i> Nuevo documento
          </button>
        </div>
      </template>
    </TopBarTitleComponent>

    <b-container fluid class="pb-4 px-lg-4">
      <!-- Barra de búsqueda + filtros -->
      <div class="doc-filter-bar mb-3">
        <el-input
          v-model="searchQuery"
          placeholder="Buscar documento..."
          suffix-icon="el-icon-search"
          clearable
          class="doc-filter-bar__search"
          @input="onSearch"
        />
        <FilterBetweenDatesSelect
          :defaultMonthValue="true"
          class="doc-filter-bar__date"
          @onAsyncDataForDates="onDateChange"
        />
        <el-select
          v-model="activeStatus"
          placeholder="Estado"
          clearable
          class="doc-filter-bar__status"
          @change="filterByStatus(activeStatus)"
        >
          <el-option
            v-for="tab in statusOptions"
            :key="tab.key"
            :value="tab.key"
            :label="tab.label"
          >
            <span>{{ tab.label }}</span>
            <span v-if="tab.count > 0" class="doc-filter-option-count">{{
              tab.count
            }}</span>
          </el-option>
        </el-select>
      </div>

      <!-- Loading inicial -->
      <template v-if="loading && !documents.length">
        <SkeletonLoadingCard />
      </template>

      <!-- Listado de documentos -->
      <div class="doc-grid">
        <div
          class="doc-grid-card"
          v-for="doc in documents"
          :key="doc.id"
          :data-doc-id="doc.id"
          @click="
            doc.status === 'draft'
              ? openPreview(doc)
              : goRouter('documentos.detail', { id: doc.id })
          "
        >
          <div class="doc-grid-card__thumb">
            <canvas
              v-show="thumbState[doc.id] === 'loaded'"
              :ref="'thumbCanvas' + doc.id"
            />
            <div
              v-if="!thumbState[doc.id] || thumbState[doc.id] === 'loading'"
              class="doc-grid-card__thumb-loading"
            >
              <i class="el-icon-loading" />
            </div>
            <div
              v-if="thumbState[doc.id] === 'error'"
              class="doc-grid-card__thumb-fallback"
              :class="'doc-grid-card__thumb-fallback--' + doc.status"
            >
              <i class="el-icon-document" />
            </div>
            <span
              class="doc-badge doc-grid-card__badge"
              :class="'doc-badge--' + doc.status"
            >
              {{ readStatus(doc.status) }}
            </span>
            <el-dropdown
              trigger="click"
              popper-class="doc-card-dropdown"
              class="doc-grid-card__menu"
              @command="handleCommand($event, doc)"
              @click.native.stop
            >
              <span class="doc-grid-card__menu-btn">
                <i class="el-icon-more" />
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="view">Ver</el-dropdown-item>
                <el-dropdown-item command="edit" v-if="doc.status === 'draft'"
                  >Firmar</el-dropdown-item
                >
                <el-dropdown-item
                  command="edit-pdf"
                  v-if="doc.status === 'draft' && doc.source === 'upload'"
                  >Editar PDF</el-dropdown-item
                >
                <el-dropdown-item command="duplicate"
                  >Duplicar</el-dropdown-item
                >
                <el-dropdown-item command="download"
                  >Descargar</el-dropdown-item
                >
                <el-dropdown-item
                  command="cancel"
                  v-if="
                    ['sent', 'viewed', 'partially_signed'].includes(doc.status)
                  "
                >
                  <span style="color:#e24b4a">Cancelar envío</span>
                </el-dropdown-item>
                <el-dropdown-item
                  command="archive"
                  v-if="
                    [
                      'signed',
                      'downloaded',
                      'owner_signed',
                      'cancelled',
                      'expired'
                    ].includes(doc.status)
                  "
                  divided
                >
                  <span style="color:#989aae"
                    ><i class="el-icon-folder" style="margin-right:6px"></i
                    >Archivar</span
                  >
                </el-dropdown-item>
                <el-dropdown-item
                  command="delete"
                  v-if="doc.status === 'draft'"
                  divided
                >
                  <span style="color:#e24b4a">Eliminar</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <div class="doc-grid-card__info">
            <p class="doc-grid-card__name" :title="doc.name">
              {{ doc.name }}
            </p>
            <p class="doc-grid-card__meta">
              <span v-if="doc.quote">Cotización #{{ doc.quote.code }} · </span>
              {{ doc.created_at }}
            </p>
          </div>
        </div>
      </div>

      <!-- Infinite scroll — siempre montado para disparar en el arranque -->
      <infinite-loading
        @infinite="infiniteHandler"
        ref="infiniteLoading"
        :identifier="infiniteId"
      >
        <div slot="spinner">
          <SkeletonLoadingCard />
        </div>
        <div slot="no-more"></div>
        <div slot="no-results">
          <!-- Only show empty state when there are truly no documents loaded.
               vue-infinite-loading fires no-results when complete() is called
               without a prior loaded() — this happens on single-page result sets
               even when items were fetched. -->
          <template v-if="!loading && !documents.length">
            <EmptyPage
              text="Aún no tienes documentos. Crea contratos, NDAs o propuestas y envíalos a firmar."
            />
            <div class="text-center mt-3">
              <button
                v-if="$can('documents.sign')"
                class="btn md__btn-primary"
                @click="goRouter('documentos.new')"
              >
                Crear tu primer documento
              </button>
            </div>
          </template>
        </div>
      </infinite-loading>

      <!-- Plantillas rápidas -->
      <div class="doc-quick-templates mt-4" v-if="templates.length">
        <p class="doc-quick-templates__title">Plantillas rápidas</p>
        <div class="doc-quick-templates__list">
          <button
            class="doc-quick-templates__pill"
            v-for="tpl in templates"
            :key="tpl.id"
            @click="goRouter('documentos.new', {}, { template: tpl.id })"
          >
            {{ tpl.name }}
          </button>
          <button
            class="doc-quick-templates__pill doc-quick-templates__pill--add"
            @click="goRouter('documentos.new')"
          >
            + Crear plantilla
          </button>
        </div>
      </div>
    </b-container>

    <WrapperDocumentsSignTourComponent />

    <DocumentPreviewModal
      :visible.sync="previewVisible"
      :document="previewDoc"
      :pdf-data="previewPdfBuffer"
      :loading="previewLoading"
      @edit-pdf="goEditPdfFromPreview"
      @sign="goSignFromPreview"
    />
  </div>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";
import EmptyPage from "../../components/EmptyPage";
import SkeletonLoadingCard from "@/components/Animations/Loading/SkeletonCard";
import FilterBetweenDatesSelect from "../../components/FilterBetweenDatesSelect";
import WrapperDocumentsSignTourComponent from "../components/WrapperTour";
import DocumentPreviewModal from "../components/DocumentPreviewModal.vue";

import documentoService from "../services/documentoService";

export default {
  name: "DocumentListPage",
  components: {
    TopBarTitleComponent,
    EmptyPage,
    SkeletonLoadingCard,
    FilterBetweenDatesSelect,
    WrapperDocumentsSignTourComponent,
    DocumentPreviewModal
  },
  data() {
    return {
      loading: true,
      documents: [],
      templates: [],
      searchQuery: "",
      activeStatus: null,
      dateFrom: null,
      dateTo: null,
      statusCounts: {},
      page: 1,
      perPage: 20,
      hasMore: true,
      breakScroll: false,
      infiniteId: +new Date(),
      debounceTimer: null,
      thumbState: {},
      observedThumbIds: new Set(),
      thumbObserver: null,
      previewVisible: false,
      previewDoc: null,
      previewPdfBuffer: null,
      previewLoading: false
    };
  },
  computed: {
    statusOptions() {
      return [
        {
          key: "draft",
          label: "Borradores",
          count: this.statusCounts.draft || 0
        },
        {
          key: "owner_signed",
          label: "Firmados por ti",
          count: this.statusCounts.owner_signed || 0
        },
        {
          key: "sent",
          label: "Pendientes firma",
          count: this.statusCounts.sent || 0
        },
        {
          key: "signed",
          label: "Completados",
          count: this.statusCounts.signed || 0
        },
        {
          key: "expired",
          label: "Expirados",
          count: this.statusCounts.expired || 0
        },
        {
          key: "cancelled",
          label: "Cancelados",
          count: this.statusCounts.cancelled || 0
        },
        {
          key: "archived",
          label: "Archivados",
          count: this.statusCounts.archived || 0
        }
      ];
    }
  },
  methods: {
    goRouter(name, params = {}, query = {}) {
      this.$router.push({ name, params, query });
    },
    readStatus(status) {
      const map = {
        draft: "Borrador",
        owner_signed: "Firmado por ti",
        sent: "Enviado",
        viewed: "Visto",
        partially_signed: "Parcial",
        signed: "Completado",
        downloaded: "Descargado",
        expired: "Expirado",
        cancelled: "Cancelado"
      };
      return map[status] || status;
    },
    resetList() {
      this.page = 1;
      this.documents = [];
      this.hasMore = true;
      this.breakScroll = false;
      this.infiniteId += 1; // force infinite-loading to reset
      this.thumbState = {};
      this.observedThumbIds.clear();
    },
    filterByStatus(status) {
      this.activeStatus = status || null;
      this.resetList();
    },
    onSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.resetList();
      }, 400);
    },
    onDateChange({ searchDates } = {}) {
      this.dateFrom = searchDates?.from || null;
      this.dateTo = searchDates?.to || null;
      this.resetList();
    },
    infiniteHandler($state) {
      if (this.breakScroll) return;
      this.breakScroll = true;
      this.getDocuments($state);
    },
    async getDocuments($state) {
      try {
        if (this.page === 1) this.loading = true;

        const params = {
          page: this.page,
          limit: this.perPage
        };
        if (this.activeStatus) params.status = this.activeStatus;
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.dateFrom) params.from = this.dateFrom;
        if (this.dateTo) params.to = this.dateTo;
        // Exclude archived unless explicitly filtering by archived
        if (this.activeStatus !== "archived") params.exclude_archived = 1;

        const response = await documentoService.getDocuments({ params });
        const { success, data } = response.data || {};
        // El interceptor de Api.js "silencia" los GET cancelados por deduplicación
        // devolviendo { data: {} }: llega sin `success` ni `data`. Sin este guard
        // caería en el else y marcaría la lista como completa, así que el
        // infinite-loading dejaría de pedir páginas hasta recargar la página entera.
        if (success === undefined && data === undefined) {
          if ($state) $state.loaded();
          return;
        }

        if (success) {
          const items = data.data || [];
          items.forEach(item => this.documents.push(item));
          this.page += 1;

          if (items.length < this.perPage || !data.next_page_url) {
            if ($state) $state.complete();
          } else {
            if ($state) $state.loaded();
          }
        } else {
          if ($state) $state.complete();
        }
      } catch (error) {
        if ($state) $state.complete();
      } finally {
        this.loading = false;
        this.breakScroll = false;
      }
    },
    async getTemplates() {
      try {
        const response = await documentoService.getTemplates({
          skipErrorNotification: true
        });
        const { success, data } = response.data;
        if (success) {
          this.templates = data || [];
        }
      } catch (error) {
        // Templates are optional
      }
    },
    async handleCommand(command, doc) {
      switch (command) {
        case "view":
          if (doc.status === "draft") {
            this.openPreview(doc);
          } else {
            this.goRouter("documentos.detail", { id: doc.id });
          }
          break;
        case "edit":
          // Draft PDFs go directly to the sign flow to continue the process
          if (doc.status === "draft") {
            this.goRouter("documentos.sign-flow", {}, { documentId: doc.id });
          } else {
            this.goRouter("documentos.edit", { id: doc.id });
          }
          break;
        case "edit-pdf":
          this.goRouter("documentos.pdf-edit", { id: doc.id });
          break;
        case "duplicate":
          try {
            await documentoService.duplicateDocument(doc.id);
            this.$notifications.success({ message: "Documento duplicado" });
            this.getDocuments();
          } catch (e) {
            this.$notifications.error({ message: "Error al duplicar" });
          }
          break;
        case "download":
          this.downloadPdf(doc);
          break;
        case "cancel":
          this.$confirm(
            "¿Cancelar este documento? Los firmantes pendientes serán notificados.",
            "Cancelar documento",
            {
              confirmButtonText: "Sí, cancelar",
              cancelButtonText: "No",
              type: "warning"
            }
          )
            .then(async () => {
              try {
                await documentoService.cancelDocument(doc.id);
                this.$notifications.success({ message: "Documento cancelado" });
                this.getDocuments();
              } catch (e) {
                this.$notifications.error({ message: "Error al cancelar" });
              }
            })
            .catch(() => {});
          break;
        case "archive":
          this.$confirm(
            "El documento se archivará y no aparecerá en la lista principal. Puedes verlo filtrando por 'Archivados'.",
            "Archivar documento",
            {
              confirmButtonText: "Archivar",
              cancelButtonText: "Cancelar",
              type: "info"
            }
          )
            .then(async () => {
              try {
                await documentoService.archiveDocument(doc.id);
                this.$notifications.success({ message: "Documento archivado" });
                this.documents = this.documents.filter(d => d.id !== doc.id);
              } catch (e) {
                this.$notifications.error({ message: "Error al archivar" });
              }
            })
            .catch(() => {});
          break;
        case "delete":
          this.$confirm("¿Eliminar este documento?", "Confirmar", {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning"
          })
            .then(async () => {
              try {
                await documentoService.deleteDocument(doc.id);
                this.$notifications.success({ message: "Documento eliminado" });
                this.documents = this.documents.filter(d => d.id !== doc.id);
              } catch (e) {
                this.$notifications.error({ message: "Error al eliminar" });
              }
            })
            .catch(() => {});
          break;
      }
    },
    async downloadPdf(doc) {
      try {
        const response = await documentoService.downloadDocument(doc.id);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const isSigned = ["signed", "downloaded", "owner_signed"].includes(
          doc.status
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `${doc.name}${isSigned ? "_firmado" : ""}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$notifications.error({ message: "Error al descargar" });
      }
    },

    // ─── Preview modal (draft documents) ──────────────────────────────
    async openPreview(doc) {
      this.previewDoc = doc;
      this.previewVisible = true;
      this.previewLoading = true;
      this.previewPdfBuffer = null;
      try {
        const response = await documentoService.previewPdf(doc.id, {
          skipErrorNotification: true
        });
        if (response && response.data) {
          this.previewPdfBuffer = new Uint8Array(response.data);
        }
      } catch (e) {
        this.previewPdfBuffer = null;
      } finally {
        this.previewLoading = false;
      }
    },

    goEditPdfFromPreview() {
      const doc = this.previewDoc;
      this.previewVisible = false;
      this.goRouter("documentos.pdf-edit", { id: doc.id });
    },

    goSignFromPreview() {
      const doc = this.previewDoc;
      this.previewVisible = false;
      this.goRouter("documentos.sign-flow", {}, { documentId: doc.id });
    },

    // ─── Thumbnails (lazy, vía IntersectionObserver) ──────────────────
    observeThumbCards() {
      if (!this.thumbObserver || !this.$el || !this.$el.querySelectorAll)
        return;
      const cards = this.$el.querySelectorAll(".doc-grid-card[data-doc-id]");
      cards.forEach(card => {
        const id = Number(card.dataset.docId);
        if (this.observedThumbIds.has(id)) return;
        this.observedThumbIds.add(id);
        this.thumbObserver.observe(card);
      });
    },
    loadPdfScript() {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) return resolve(window.pdfjsLib);
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          const lib = window.pdfjsLib;
          lib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          resolve(lib);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async loadThumbnail(doc) {
      if (this.thumbState[doc.id]) return;
      this.$set(this.thumbState, doc.id, "loading");
      try {
        const response = await documentoService.previewPdf(doc.id, {
          skipErrorNotification: true
        });
        if (!response.data || !response.data.byteLength) {
          this.$set(this.thumbState, doc.id, "error");
          return;
        }
        const pdfjsLib = await this.loadPdfScript();
        const typedArray = new Uint8Array(response.data);
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
        const page = await pdf.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = 260 / baseViewport.width;
        const viewport = page.getViewport({ scale });

        await this.$nextTick();
        const ref = this.$refs["thumbCanvas" + doc.id];
        const canvas = Array.isArray(ref) ? ref[0] : ref;
        if (!canvas) {
          this.$set(this.thumbState, doc.id, "error");
          return;
        }
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;
        this.$set(this.thumbState, doc.id, "loaded");
      } catch (e) {
        this.$set(this.thumbState, doc.id, "error");
      }
    }
  },
  mounted() {
    this.getTemplates();
    this.thumbObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this.thumbObserver.unobserve(entry.target);
          const id = Number(entry.target.dataset.docId);
          const doc = this.documents.find(d => d.id === id);
          if (doc) this.loadThumbnail(doc);
        });
      },
      { rootMargin: "200px", threshold: 0.01 }
    );
  },
  updated() {
    this.$nextTick(() => this.observeThumbCards());
  },
  beforeDestroy() {
    if (this.thumbObserver) this.thumbObserver.disconnect();
  }
};
</script>

<style lang="scss" scoped>
.doc-btn-validate,
.doc-btn-primary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: 38px;
  box-sizing: border-box;
}
.doc-btn-validate {
  border: 1.5px solid #e4eaf6;
  background: #fff;
  color: #31355c;
  i {
    font-size: 16px;
    color: #3fdbbf;
  }
  &:hover {
    border-color: #3fdbbf;
    background: #f0fdf9;
  }
}
.doc-btn-primary {
  border: none;
  background: #3fdbbf;
  color: #fff;
  font-weight: 600;
  i {
    font-size: 16px;
  }
  &:hover {
    background: #30c5aa;
  }
}
.doc-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  &__search {
    flex: 1;
    min-width: 180px;
  }

  &__date {
    width: 200px;
    flex-shrink: 0;
  }

  &__status {
    width: 180px;
    flex-shrink: 0;
  }
}

.doc-filter-option-count {
  float: right;
  font-size: 11px;
  color: #989aae;
  background: #f0f2f5;
  border-radius: 10px;
  padding: 1px 8px;
  margin-left: 8px;
  min-width: 22px;
  text-align: center;
}

/* Legacy tab styles kept as fallback */
.doc-tab {
  border: none;
  background: #e4eaf6;
  color: #7e819f;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 18px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.25s;
  &:hover {
    background: #d6dff3;
  }
  &--active {
    background: #31355c;
    color: #fff;
  }
  &__count {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
    margin-left: 4px;
  }
}
.doc-search {
  max-width: 340px;
  ::v-deep .el-input__inner {
    border: 1.5px solid #e4eaf6;
    border-radius: 10px;
    background: #fafbfd;
    font-size: 14px;
    min-height: 44px;
    &:focus {
      border-color: #3fdbbf;
      background: #fff;
    }
  }
}
.doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 18px;
  margin-bottom: 20px;
}
.doc-grid-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
  box-shadow: 0 0 10px 0 #d6e4f3;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px 0 rgba(214, 228, 243, 0.7);
  }

  &__thumb {
    position: relative;
    height: 190px;
    background: #f4f6fb;
    border-bottom: 1px solid #eef1f8;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    canvas {
      width: 100%;
      height: auto;
      display: block;
    }
  }
  &__thumb-loading,
  &__thumb-fallback {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    color: #c3c9d9;
  }
  &__thumb-fallback {
    &--draft {
      color: #989aae;
    }
    &--sent,
    &--viewed,
    &--partially_signed {
      color: #c99a4a;
    }
    &--owner_signed {
      color: #1d9e75;
    }
    &--signed,
    &--downloaded {
      color: #0f6e56;
    }
    &--expired {
      color: #a32d2d;
    }
    &--cancelled {
      color: #989aae;
    }
  }

  &__badge {
    position: absolute;
    top: 8px;
    left: 8px;
  }

  &__menu {
    position: absolute;
    top: 6px;
    right: 6px;
  }
  &__menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    cursor: pointer;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 1px 4px 0 rgba(30, 28, 66, 0.15);
    &:hover {
      background: #fff;
    }
  }

  &__info {
    padding: 10px 12px 12px;
    min-width: 0;
  }
  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #1e1c42 !important;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__meta {
    font-size: 11px;
    color: #989aae;
    margin: 4px 0 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.doc-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
  &--draft {
    background: #e4eaf6;
    color: #7e819f;
  }
  &--sent,
  &--viewed,
  &--partially_signed {
    background: #faeeda;
    color: #854f0b;
  }
  &--owner_signed {
    background: #e0f7f1;
    color: #1d9e75;
  }
  &--signed,
  &--downloaded {
    background: #e1f5ee;
    color: #0f6e56;
  }
  &--expired {
    background: #fcebeb;
    color: #a32d2d;
  }
  &--cancelled {
    background: #e4eaf6;
    color: #7e819f;
  }
}

.doc-quick-templates {
  background: #f7f8f9;
  border-radius: 16px;
  padding: 20px;
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #31355c;
    margin: 0 0 12px;
  }
  &__list {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  &__pill {
    border: 1.5px solid #e4eaf6;
    background: #fff;
    color: #31355c;
    font-size: 13px;
    padding: 8px 16px;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
      border-color: #3fdbbf;
      color: #31355c;
      background: #f0fdf9;
    }
    &--add {
      border-style: dashed;
      color: #989aae;
    }
  }
}
</style>

<style>
/* DocumentList dropdown — targets .el-dropdown-menu rendered by el-dropdown */
/* Note: popper-class="doc-card-dropdown" may not apply in all El-UI 2.x versions  */
/* so --divided fix is handled globally in main.scss                                 */
.el-dropdown-menu.el-popper .el-dropdown-menu__item {
  padding: 8px 20px;
  font-size: 13px;
  line-height: 1.6;
  white-space: nowrap;
}
</style>
